import { normalizeSource, type AnalyticsAction } from "./analytics-shared";

const VISITOR_KEY = "wv_visitor";
const SESSION_KEY = "wv_session";
const SOURCE_KEY = "wv_source";

function randomId() {
  try {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 24);
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function readStore(store: Storage | undefined, key: string) {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStore(store: Storage | undefined, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    // private mode, ignore
  }
}

function visitorId() {
  if (typeof window === "undefined") return null;
  let id = readStore(window.localStorage, VISITOR_KEY);
  if (!id) {
    id = randomId();
    writeStore(window.localStorage, VISITOR_KEY, id);
  }
  return id;
}

function sessionId() {
  if (typeof window === "undefined") return null;
  let id = readStore(window.sessionStorage, SESSION_KEY);
  if (!id) {
    id = randomId();
    writeStore(window.sessionStorage, SESSION_KEY, id);
  }
  return id;
}

function deviceType() {
  if (typeof window === "undefined") return null;
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function referrerHost() {
  if (typeof document === "undefined" || !document.referrer) return null;
  try {
    const host = new URL(document.referrer).hostname;
    if (host === window.location.hostname) return null;
    return host;
  } catch {
    return null;
  }
}

const SOCIAL_HOSTS = [
  "facebook",
  "instagram",
  "twitter",
  "t.co",
  "x.com",
  "linkedin",
  "tiktok",
  "whatsapp",
  "reddit",
  "pinterest",
];
const SEARCH_HOSTS = ["google", "bing", "duckduckgo", "yahoo", "ecosia", "brave"];

/**
 * Resolves the traffic source once per session: an explicit `?src=` tag wins,
 * then the referrer classification, then direct. Sticky so a claim minutes
 * after a QR scan is still credited to the QR code.
 */
export function resolveSource() {
  if (typeof window === "undefined") return "direct";
  const tagged = normalizeSource(new URLSearchParams(window.location.search).get("src"));
  if (tagged) {
    writeStore(window.sessionStorage, SOURCE_KEY, tagged);
    return tagged;
  }
  const stored = readStore(window.sessionStorage, SOURCE_KEY);
  if (stored) return stored;

  const host = referrerHost();
  let resolved = "direct";
  if (host) {
    if (SOCIAL_HOSTS.some((s) => host.includes(s))) resolved = "social";
    else if (SEARCH_HOSTS.some((s) => host.includes(s))) resolved = "search";
    else resolved = "referral";
  }
  writeStore(window.sessionStorage, SOURCE_KEY, resolved);
  return resolved;
}

export type TrackOptions = {
  slug?: string | null;
  slotId?: string | null;
  channel?: string | null;
  metadata?: Record<string, string | number | boolean>;
};

/** Fire-and-forget beacon. Never blocks or throws. */
export function track(action: AnalyticsAction, options: TrackOptions = {}) {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({
    action,
    slug: options.slug ?? null,
    slotId: options.slotId ?? null,
    channel: options.channel ?? null,
    source: resolveSource(),
    referrerHost: referrerHost(),
    device: deviceType(),
    visitorId: visitorId(),
    sessionId: sessionId(),
    metadata: options.metadata ?? {},
  });

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/public/track", new Blob([body], { type: "application/json" }));
      return;
    }
  } catch {
    // fall through to fetch
  }
  void fetch("/api/public/track", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}
